import { QueryCombinerType } from 'module/api/filter/types';
import { SourceType } from 'module/api/filter/types';
import { useTranslate } from 'module/hooks/use-translation';
import { FieldType } from 'module/types/filter-field/field/field';
import { SelectFilterOperationType } from 'module/types/filter-field/filter/select';
import { GroupedFilter as GroupedFilterType } from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Loading } from 'module/ui/components/atoms/loading';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Dropdown } from 'antd';
import { useSignalCustomFieldsAsFilterableFields } from 'helpers/fields/signal-fields/custom-fields';
import { useSignalNativeFieldsAsFilterableFields } from 'helpers/fields/signal-fields/default-fields';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { FilterButton } from 'components/common/styles';
import { SignalItem } from './signal-item';
import {
  SignalsDropdownMenu,
  SignalsList,
  DropdownHeader,
  StyledSearchInput,
} from './styles';

interface SignalListDropdownProps {
  filter?: GroupedFilterType;
  onChange?: (filter: GroupedFilterType) => void;
  selectedSignals: string[];
  setSelectedSignals: (signals: string[]) => void;
}

interface SignalData {
  signalConfigurationId: number;
  signalName: string;
  fields: any[];
}

interface SignalReasonMapping {
  signalId: string;
  reasonValues: number[];
}

const isSignalConfigurationField = (field: any) => {
  return field.fieldName === 'signalConfigurationId';
};

const isSignalReasonField = (field: any) => {
  return field.fieldName === 'Reason';
};

const useSignalData = () => {
  const { data: signalConfigurations, isLoading } =
    useGetAllSignalConfigurationsQuery();
  const { fields: signalFields } = useSignalNativeFieldsAsFilterableFields();
  const { fields: reasonFields } = useSignalCustomFieldsAsFilterableFields();

  const signalConfigurationField = useMemo(
    () => signalFields.find((field) => isSignalConfigurationField(field)),
    [signalFields],
  );

  const signalReasonField = useMemo(
    () => reasonFields.find((field) => isSignalReasonField(field)),
    [reasonFields],
  );

  return {
    signalConfigurations,
    isLoading,
    signalConfigurationField,
    signalReasonField,
  };
};

const useReasonMappings = () => {
  const [reasonMappings, setReasonMappings] = useState<SignalReasonMapping[]>(
    [],
  );

  const getSelectedReasonsForSignal = useCallback(
    (signalId: string) => {
      const mapping = reasonMappings.find((m) => m.signalId === signalId);

      return mapping?.reasonValues || [];
    },
    [reasonMappings],
  );

  const syncReasonMappingsFromFilter = useCallback(
    (
      filter: GroupedFilterType | undefined,
      signalConfigurations: any[],
      getAllReasonsForSignal: (signal: SignalData) => any[],
    ) => {
      if (!filter) {
        return;
      }

      const reasonCriteria = filter.groupCriteria?.find(
        (criteria) =>
          'field' in criteria.criteria &&
          criteria.criteria.field.fieldName === 'Reason',
      );

      if (reasonCriteria && 'value' in reasonCriteria.criteria) {
        const reasonValues = reasonCriteria.criteria.value as number[];
        const newMappings: SignalReasonMapping[] = [];

        signalConfigurations?.forEach((signal) => {
          const signalReasons = getAllReasonsForSignal(signal);
          const signalReasonValues = signalReasons.map(
            (reason: any) => reason.value,
          );

          const signalSelectedReasons = reasonValues.filter((value) =>
            signalReasonValues.includes(value),
          );

          if (
            signalSelectedReasons.length > 0 &&
            signalSelectedReasons.length < signalReasonValues.length
          ) {
            newMappings.push({
              signalId: signal.signalConfigurationId.toString(),
              reasonValues: signalSelectedReasons,
            });
          }
        });

        setReasonMappings(newMappings);
      } else {
        setReasonMappings([]);
      }
    },
    [],
  );

  return {
    reasonMappings,
    getSelectedReasonsForSignal,
    setReasonMappings,
    syncReasonMappingsFromFilter,
  };
};

const updateFilterWithCriteria = (
  value: GroupedFilterType | undefined,
  onChange: ((filter: GroupedFilterType) => void) | undefined,
  signalConfigurationField: any,
  signalReasonField: any,
  signalConfigurations: any[],
  newSelectedSignals: string[],
  newReasonMappings: SignalReasonMapping[],
  //   getAllReasonsForSignal: (signal: SignalData) => any[],
) => {
  const updatedGroupCriteria = (value?.groupCriteria || []).filter(
    (criteria) =>
      'field' in criteria.criteria &&
      !isSignalConfigurationField(criteria.criteria.field) &&
      !isSignalReasonField(criteria.criteria.field),
  );

  if (newSelectedSignals.length > 0 && signalConfigurationField) {
    updatedGroupCriteria.push({
      id: FilterGroupType.Signal,
      criteria: {
        field: signalConfigurationField,
        type: SelectFilterOperationType.IsOneOf,
        value: newSelectedSignals.map((id) => parseInt(id)),
      } as any,
    });
  }

  // Consolidate all reason values into a single criteria instead of creating multiple
  if (newReasonMappings.length > 0 && signalReasonField) {
    // Collect all unique reason values from all mappings
    const allReasonValues = new Set<number>();

    newReasonMappings.forEach((mapping) => {
      mapping.reasonValues.forEach((reasonValue) => {
        allReasonValues.add(reasonValue);
      });
    });

    // Only add reason criteria if there are reason values to filter by
    if (allReasonValues.size > 0) {
      updatedGroupCriteria.push({
        id: FilterGroupType.Signal,
        hidden: true,
        criteria: {
          field: {
            ...signalReasonField,
            fieldType: FieldType.MultiSelect,
            sourceType: SourceType.Signal,
          },
          type: SelectFilterOperationType.IsOneOf,
          value: Array.from(allReasonValues),
        } as any,
      });
    }
  }

  const updatedFilter = {
    ...value,
    type: value?.type || QueryCombinerType.All,
    defaultGroup: value?.defaultGroup || FilterGroupType.Company,
    groupCriteria: updatedGroupCriteria,
  };

  onChange?.(updatedFilter);
};

export const SignalListDropdown: React.FC<SignalListDropdownProps> = ({
  filter: value,
  onChange,
  selectedSignals,
  setSelectedSignals,
}) => {
  const [showAllSignals, setShowAllSignals] = useState(false);
  const [search, setSearch] = useState('');
  const translate = useTranslate();

  const {
    signalConfigurations,
    isLoading,
    signalConfigurationField,
    signalReasonField,
  } = useSignalData();
  const {
    reasonMappings,
    getSelectedReasonsForSignal,
    setReasonMappings,
    syncReasonMappingsFromFilter,
  } = useReasonMappings();

  const isSelected = useCallback(
    (signalId: string) => selectedSignals.includes(signalId),
    [selectedSignals],
  );

  const updatedSelectedSignals = useCallback(
    (signalId: string) => {
      const newSelectedSignals = isSelected(signalId)
        ? selectedSignals.filter((id) => id !== signalId)
        : [...selectedSignals, signalId];

      return newSelectedSignals;
    },
    [selectedSignals, isSelected],
  );

  const getAllReasonsForSignal = useCallback((signal: SignalData) => {
    const reasons =
      signal.fields.find(
        (f: any) =>
          f.metaData && f.metaData.options && Array.isArray(f.metaData.options),
      )?.metaData?.options || [];

    // Use indexing logic for reason options
    //   const signalIndex =
    //     signalConfigurations?.findIndex(
    //       (s) => s.signalConfigurationId === signal.signalConfigurationId,
    //     ) || 0;

    //   let startingValue = 1;

    //   for (let i = 0; i < signalIndex; i++) {
    //     const prevSignal = signalConfigurations?.[i];

    //     if (prevSignal) {
    //       const prevReasons =
    //         prevSignal.fields.find(
    //           (f: any) =>
    //             f.metaData &&
    //             f.metaData.options &&
    //             Array.isArray(f.metaData.options),
    //         )?.metaData?.options || [];

    //       startingValue += prevReasons.length;
    //     }
    //   }

    //   return reasons.map((reason: any, index: number) => ({
    //     ...reason,
    //     value: startingValue + index,
    //   }));
    return reasons;
  }, []);

  useEffect(() => {
    if (signalConfigurations) {
      syncReasonMappingsFromFilter(
        value,
        signalConfigurations,
        getAllReasonsForSignal,
      );
    }
  }, [
    value,
    signalConfigurations,
    syncReasonMappingsFromFilter,
    getAllReasonsForSignal,
  ]);

  const handleSignalToggle = useCallback(
    (signalId: string, reasonValues?: number[]) => {
      const newSelectedSignals = updatedSelectedSignals(signalId);

      if (isSelected(signalId)) {
        const updatedMappings = reasonMappings.filter(
          (mapping) => mapping.signalId !== signalId,
        );

        setReasonMappings(updatedMappings);
        updateFilterWithCriteria(
          value,
          onChange,
          signalConfigurationField,
          signalReasonField,
          signalConfigurations || [],
          newSelectedSignals,
          updatedMappings,
          //   getAllReasonsForSignal,
        );
      } else {
        const signal = signalConfigurations?.find(
          (s) => s.signalConfigurationId.toString() === signalId,
        );

        if (signal) {
          const allReasons = getAllReasonsForSignal(signal);
          const allReasonValues = allReasons.map((reason: any) => reason.value);

          const finalReasonValues = reasonValues || allReasonValues;

          if (finalReasonValues.length === allReasonValues.length) {
            updateFilterWithCriteria(
              value,
              onChange,
              signalConfigurationField,
              signalReasonField,
              signalConfigurations || [],
              newSelectedSignals,
              reasonMappings,
            );
          } else {
            const newMappings = [
              ...reasonMappings,
              {
                signalId,
                reasonValues: finalReasonValues,
              },
            ];

            setReasonMappings(newMappings);
            updateFilterWithCriteria(
              value,
              onChange,
              signalConfigurationField,
              signalReasonField,
              signalConfigurations || [],
              newSelectedSignals,
              newMappings,
              //   getAllReasonsForSignal,
            );
          }
        } else {
          updateFilterWithCriteria(
            value,
            onChange,
            signalConfigurationField,
            signalReasonField,
            signalConfigurations || [],
            newSelectedSignals,
            reasonMappings,
            // getAllReasonsForSignal,
          );
        }
      }

      setSelectedSignals(newSelectedSignals);
    },
    [
      setSelectedSignals,
      reasonMappings,
      isSelected,
      setReasonMappings,
      signalConfigurations,
      updatedSelectedSignals,
      getAllReasonsForSignal,
      value,
      onChange,
      signalConfigurationField,
      signalReasonField,
    ],
  );

  const handleReasonToggle = useCallback(
    (signalId: string, reasonValues: number[]) => {
      const newSelectedSignals =
        reasonValues.length > 0
          ? [...new Set([...selectedSignals, signalId])]
          : selectedSignals.filter((id) => id !== signalId);

      let updatedMappings = reasonMappings.filter(
        (mapping) => mapping.signalId !== signalId,
      );

      if (reasonValues.length > 0) {
        const signal = signalConfigurations?.find(
          (s) => s.signalConfigurationId.toString() === signalId,
        );

        if (signal) {
          const allReasons = getAllReasonsForSignal(signal);
          const allReasonValues = allReasons.map((reason: any) => reason.value);

          if (reasonValues.length < allReasonValues.length) {
            const newMapping: SignalReasonMapping = {
              signalId,
              reasonValues,
            };

            updatedMappings.push(newMapping);
          }
        }
      }

      setReasonMappings(updatedMappings);
      updateFilterWithCriteria(
        value,
        onChange,
        signalConfigurationField,
        signalReasonField,
        signalConfigurations || [],
        newSelectedSignals,
        updatedMappings,
        // getAllReasonsForSignal,
      );
      setSelectedSignals(newSelectedSignals);
    },
    [
      selectedSignals,
      setSelectedSignals,
      reasonMappings,
      signalConfigurations,
      setReasonMappings,
      getAllReasonsForSignal,
      value,
      onChange,
      signalConfigurationField,
      signalReasonField,
    ],
  );

  return (
    <Dropdown
      overlay={
        <SignalsDropdownMenu>
          <DropdownHeader>
            {translate('all_signals.dropdown.title')}
          </DropdownHeader>
          <StyledSearchInput
            placeholder={translate('search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
          <SignalsList>
            {isLoading ? (
              <Loading />
            ) : (
              <>
                {signalConfigurations?.map((signal: SignalData) => (
                  <SignalItem
                    key={signal.signalConfigurationId}
                    signal={signal}
                    selectedSignals={selectedSignals}
                    getAllReasonsForSignal={getAllReasonsForSignal}
                    getSelectedReasonsForSignal={getSelectedReasonsForSignal}
                    onSignalToggle={(signalId, reasonValues) =>
                      handleSignalToggle(signalId, reasonValues)
                    }
                    onReasonToggle={(signalId, reasonValues) =>
                      handleReasonToggle(signalId, reasonValues)
                    }
                  />
                ))}
              </>
            )}
          </SignalsList>
        </SignalsDropdownMenu>
      }
      trigger={['click']}
    >
      <FilterButton
        type="button"
        leftIcon={<FontAwesomeIcon icon={faPlus} size={16} />}
        size="medium"
        onClick={() => setShowAllSignals(!showAllSignals)}
      >
        {translate('all_signals.dropdown.title')}
      </FilterButton>
    </Dropdown>
  );
};
